import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { getContractDeliverables, setContractMilsetone } from '../../services/procurementService';
import ConfirmationModal from '../../components/ConfirmModal/ConfirmationModal';
import { toast } from 'react-toastify';

const statusMapping = {
  Pending: {
    iconSrc: `${process.env.PUBLIC_URL}/assets/images/tender-upcoming.svg`,
    classname: 'border-[#D0D5DD] text-[#D0D5DD]',
  },
  Upcoming: {
    iconSrc: `${process.env.PUBLIC_URL}/assets/images/tender-upcoming.svg`,
    classname: 'border-[#128FFF] text-[#128FFF]',
  },
  Completed: {
    iconSrc: `${process.env.PUBLIC_URL}/assets/images/tender-completed.svg`,
    classname: 'border-[#42BE65] text-[#42BE65]',
  },
  Overdue: {
    iconSrc: `${process.env.PUBLIC_URL}/assets/images/tender-rejected.svg`,
    classname: 'border-[#D0D5DD] text-[#D0D5DD]',
  },
};

interface DeliverableItemProps {
  deliverableId: string;
  milestone: string;
  dueDate: string;
  submittedDate: string;
  amount: number;
  deliverableStatus: string;
  handleRefetchDeliverables: () => void;
}

const DeliverableItem: React.FC<DeliverableItemProps> = ({
  deliverableId,
  milestone,
  dueDate,
  submittedDate,
  //   amount,
  deliverableStatus,
  handleRefetchDeliverables,
}) => {
  const isSupplier = localStorage.getItem('supplierId');

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const setMilestoneMutation = useMutation({
    mutationFn: () => setContractMilsetone({ deliverableId, status: 'Completed' }),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success(response.data.message);
      } else {
        toast.error(response.errorMessage);
      }
    },
    onError: (error) => {
      console.error(error);
      toast.error('Unable to perform action, please try again later');
    },
  });

  const handleConfirm = async () => {
    console.log('Deliverable marked as completed');
    await setMilestoneMutation.mutate();

    setConfirmationModalOpen(false);
    handleRefetchDeliverables();
  };

  return (
    <>
      <div className="relative rounded-2xl border border-[#E9EFF2] px-4 py-6">
        <div className="grid grid-cols-[2fr_1.5fr_.5fr] items-center justify-between">
          <div>
            <h4 className="mb-1 text-base font-medium text-[#292D32]">{milestone}</h4>

            <div className="flex flex-col gap-x-4 gap-y-1 xl:flex-row">
              {dueDate && (
                <p className="text-xs text-[#292D32]">
                  <span className="text-[#DA1E28]">Due Date:</span>{' '}
                  {moment(dueDate).format('DD/MM/YYYY')}
                </p>
              )}

              {submittedDate && (
                <p className="text-xs text-[#292D32]">
                  <span className="text-[#24A148]">Submitted:</span>{' '}
                  {moment(submittedDate).format('DD/MM/YYYY')}
                </p>
              )}
            </div>
          </div>

          {/* <div>
                  <p className="text-sm italic">Approved 3 months ago</p>
                  <Link to="" className="text-sm font-semibold text-[#24A148]">
                    View Submissions
                  </Link>
                </div> */}

          {isSupplier ? (
            <button
              className="w-fit rounded-2xl bg-[#24A148] px-3 py-1 text-sm font-semibold text-[#fff] disabled:cursor-not-allowed disabled:bg-gray-400"
              onClick={() => setConfirmationModalOpen(true)}
              disabled={deliverableStatus === 'Completed' || deliverableStatus === 'Pending'}
            >
              Mark as completed
            </button>
          ) : (
            <div></div>
          )}

          <div
            className={`flex h-[33px] max-w-fit items-center justify-center rounded-2xl border px-5 py-3 text-sm font-semibold ${statusMapping[deliverableStatus as keyof typeof statusMapping].classname}`}
          >
            <span>{deliverableStatus}</span>
          </div>
        </div>

        <img
          src={statusMapping[deliverableStatus as keyof typeof statusMapping].iconSrc}
          alt="Icon"
          className="absolute -left-[26px] top-[50%] h-6 w-6 -translate-x-1/2 -translate-y-1/2"
        />
      </div>

      <ConfirmationModal
        isOpen={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        onConfirm={handleConfirm}
        title="Confirm Action"
        message={`Are you sure you want to Mark this as completed?`}
      />
    </>
  );
};

const DeliverablesComponent = () => {
  const { contractId } = useParams<{ contractId: string }>();

  //   Fetch tender documents
  const { data: contractDeliverables, refetch } = useQuery({
    queryKey: ['contractById', contractId],
    queryFn: async () => {
      const response = await getContractDeliverables(contractId as string);

      return response.data;
    },
    enabled: !!contractId,
    // staleTime: 3 * 60 * 1000, // Cache data for 3 minutes
    refetchOnWindowFocus: false, // Do not refetch when the window regains focus
  });

  useEffect(() => {
    console.log('contractDeliverables: ', contractDeliverables);
  }, [contractDeliverables]);

  const handleRefetch = () => {
    refetch();
  };

  return (
    <>
      <div className="mt-[33px] flex gap-x-6">
        <div>
          <div className="relative left-[50%] top-0 h-full w-[3px] -translate-x-1/2 bg-[#E5E5E5]">
            {/* <div className="absolute top-0 h-[25%] w-full bg-[#42BE65]"></div> */}
            {/* <div className="absolute top-[25%] h-[25%] w-full bg-[#42BE65]"></div> */}
          </div>
        </div>

        <div className="flex w-full flex-col gap-y-4">
          {/* {deliverables.map((deliverable, index) => (
            <DeliverableItem key={index} {...deliverable} />
          ))} */}
          {contractDeliverables?.deliverableModels?.map((deliverable, index) => (
            <DeliverableItem
              key={index}
              {...deliverable}
              handleRefetchDeliverables={handleRefetch}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default DeliverablesComponent;
